import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { ContentProvider } from "@/lib/ContentContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import Home from "@/pages/Home";
import Platform from "@/pages/Platform";
import Channels from "@/pages/Channels";
import AudienceData from "@/pages/AudienceData";
import WhyResponsiq from "@/pages/WhyResponsiq";
import Pricing from "@/pages/Pricing";
import Company from "@/pages/Company";
import BookDemo from "@/pages/BookDemo";
import { Privacy, Terms, OptOut } from "@/pages/Legal";

const lenisRef = { current: null };

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Shell = () => {
  const rafRef = useRef(null);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    lenisRef.current = lenis;
    const raf = (time) => {
      lenis.raf(time);
      rafRef.current = requestAnimationFrame(raf);
    };
    rafRef.current = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    const id = process.env.REACT_APP_GA4_ID;
    if (!id) return;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", id);
  }, []);

  return (
    <>
      <ScrollManager />
      <Header />
      <main id="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/audience-data" element={<AudienceData />} />
          <Route path="/why-responsiq" element={<WhyResponsiq />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/company" element={<Company />} />
          <Route path="/book-demo" element={<BookDemo />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/opt-out" element={<OptOut />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster position="bottom-right" />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ContentProvider>
        <Shell />
      </ContentProvider>
    </BrowserRouter>
  );
}

export default App;
